import { useCallback, useEffect, useRef, useState } from "react";
import {
  useGetRecipeImpactDetailsByRecipeId,
  useUpdateRecipeImpactDetails
} from "../../api/recipeImpactDetails";
import { Checkbox } from "../../components/ui/common/checkbox";
import { Skeleton } from "../../components/ui/common/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from "../../components/ui/common/table";
import { RecipeVariantCol } from "../../config/constants";
import { cn } from "../../lib/utils";

const RecipeVariationTable = ({
  originalRecipeImpact,
  variantData,
  visibleCols,
  selectedVariants,
  setSelectedVariants,
  productSize,
  setProductSize,
  recipeId,
  currentImpactChange,
  readOnly = false
}: {
  originalRecipeImpact: number;
  variantData: any[];
  visibleCols: string[];
  selectedVariants: typeof variantData;
  setSelectedVariants: (data: typeof variantData) => void;
  productSize: number;
  setProductSize: (size: number) => void;
  recipeId: string;
  currentImpactChange: any;
  readOnly?: boolean;
}) => {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [debouncedProductSize, setDebouncedProductSize] = useState(productSize);
  const prevProductSizeRef = useRef(productSize);

  const { data: impactDetails, isLoading, refetch } = useGetRecipeImpactDetailsByRecipeId(recipeId);
  const { mutate: updateRecipeImpact } = useUpdateRecipeImpactDetails();

  useEffect(() => {
    setSelectedVariants(variantData);
  }, []);

  useEffect(() => {
    if (impactDetails?.productionSize != null && isInitialLoading) {
      setProductSize(impactDetails.productionSize || 0);
      setIsInitialLoading(false);
    }
  }, [impactDetails, isInitialLoading]);

  useEffect(() => {
    if (!isLoading) {
      setIsInitialLoading(false);
    }
  }, [isLoading]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedProductSize(productSize || 0);
    }, 800);

    return () => clearTimeout(handler);
  }, [productSize]);

  const calculateImpactMetrics = useCallback(
    (variant: any) => {
      const variantClimateImpact = variant?.climateImpact ?? 0;
      const currentImpactTotal = variantClimateImpact * (productSize ?? 0);
      const impactChangePercentage = Number(
        ((variantClimateImpact / (originalRecipeImpact || 1) - 1) * 100).toFixed(4)
      );
      const totalImpactChange = (
        (variantClimateImpact - (originalRecipeImpact ?? 0)) *
        (productSize ?? 0)
      ).toFixed(2);

      return {
        variantClimateImpact,
        currentImpactTotal,
        impactChangePercentage,
        totalImpactChange
      };
    },
    [originalRecipeImpact, productSize]
  );

  useEffect(() => {
    if (readOnly || isInitialLoading || selectedVariants.length === 0) return;

    if (prevProductSizeRef.current === debouncedProductSize) return;
    prevProductSizeRef.current = debouncedProductSize;

    const variantImpactDetails = selectedVariants.map(variant => {
      const { currentImpactTotal, impactChangePercentage, totalImpactChange } =
        calculateImpactMetrics(variant);

      return {
        variantId: variant._id,
        variantName: variant.name,
        currentImpactTotal,
        impactChangePercentage,
        totalImpactChange: Number(totalImpactChange)
      };
    });

    updateRecipeImpact(
      {
        _id: impactDetails?._id ?? undefined,
        recipeId,
        productionSize: debouncedProductSize,
        originalImpact: originalRecipeImpact * debouncedProductSize,
        variantImpactDetails
      },
      { onSuccess: () => refetch() }
    );
  }, [debouncedProductSize, selectedVariants, calculateImpactMetrics, isInitialLoading]);

  const handleCheckboxChange = (variant: any) => {
    if (selectedVariants.some(v => v.id === variant.id)) {
      setSelectedVariants(selectedVariants.filter(v => v.id !== variant.id));
    } else {
      setSelectedVariants([...selectedVariants, variant]);
    }
  };

  const getImpactValueClass = (value: number) =>
    value < 0 ? "tw-font-medium tw-text-success" : "tw-font-medium tw-text-foreground";

  const cellTextClass = "tw-m-0 tw-text-foreground/80";
  const impactChangeCellTextClass = "tw-m-0 tw-font-medium tw-text-foreground";
  const centeredCellClass = "tw-text-center";

  const tableHeaderData = [
    ...RecipeVariantCol.filter(col =>
      [
        "Variant Name",
        "Baseline impact (kgCO2eq/kg)",
        "Variant Impact (kgCO2eq/kg)"
      ].includes(col.name)
    ),
    ...RecipeVariantCol.filter(
      col =>
        col.name !== "" &&
        ![
          "Variant Name",
          "Baseline impact (kgCO2eq/kg)",
          "Variant Impact (kgCO2eq/kg)"
        ].includes(col.name) &&
        visibleCols.includes(col.name)
    ),
    ...RecipeVariantCol.filter(col => col.name === "")
  ];

  const renderHeaderCell = (data: (typeof RecipeVariantCol)[number], index: number) => (
    <TableHead
      key={index}
      className={cn(
        "tw-bg-primary/5 tw-font-bold tw-text-primary tw-whitespace-normal tw-break-words",
        data?.name === "Variant Name" && "tw-text-left",
        data?.name !== "Variant Name" && data?.name !== "" && "tw-text-center",
        data?.name === "" && "tw-px-6 tw-pr-12 tw-text-center"
      )}
    >
      {data?.name || ""}
    </TableHead>
  );

  const renderLoadingRows = () =>
    [...Array(5)].map((_, i) => (
      <TableRow key={i}>
        <TableCell colSpan={tableHeaderData.length}>
          <div className="tw-space-y-2 tw-py-1">
            <Skeleton className="tw-h-4 tw-w-full" />
            <Skeleton className="tw-h-4 tw-w-4/5" />
          </div>
        </TableCell>
      </TableRow>
    ));

  const renderDataRows = () =>
    variantData.map((data, index) => {
      const isChecked = selectedVariants.some(v => v.id === data.id);
      const {
        variantClimateImpact,
        currentImpactTotal,
        impactChangePercentage,
        totalImpactChange
      } = calculateImpactMetrics(data);

      return (
        <TableRow key={index} className="tw-bg-background">
          <TableCell>
            <p className={cellTextClass}>{data.variantName}</p>
          </TableCell>
          <TableCell className={centeredCellClass}>
            <p className={cellTextClass}>{currentImpactChange?.toFixed(2)}</p>
          </TableCell>
          <TableCell className={centeredCellClass}>
            <p className={cellTextClass}>{variantClimateImpact.toFixed(2)}</p>
          </TableCell>
          {visibleCols.includes("Variant Impact Total(kgCO2eq)*") && (
            <TableCell className={centeredCellClass}>
              <p className={cellTextClass}>{currentImpactTotal.toFixed(2)}</p>
            </TableCell>
          )}
          {visibleCols.includes("Impact Change %") && (
            <TableCell className={centeredCellClass}>
              <p className={cn(impactChangeCellTextClass, getImpactValueClass(impactChangePercentage))}>
                {impactChangePercentage}
              </p>
            </TableCell>
          )}
          {visibleCols.includes("Total Impact Change (kgCO2eq)*") && (
            <TableCell className={centeredCellClass}>
              <p className={cn(impactChangeCellTextClass, getImpactValueClass(Number(totalImpactChange)))}>
                {totalImpactChange}
              </p>
            </TableCell>
          )}
          <TableCell className="tw-px-6 tw-py-4 tw-pr-12 tw-text-center">
            <Checkbox
              checked={isChecked}
              onCheckedChange={() => handleCheckboxChange(data)}
              className="tw-h-5 tw-w-5 [&_svg]:tw-size-4"
            />
          </TableCell>
        </TableRow>
      );
    });

  const tableFooterContent = (
    <div className="tw-flex tw-items-center tw-justify-between">
      <p className="tw-m-0 tw-text-sm tw-text-foreground/65">
        * Calculated based on production volume
      </p>
    </div>
  );

  return (
    <div className="tw-w-full my-1">
      <div
        data-testid="common-table"
        className="tw-overflow-hidden tw-rounded-md tw-border tw-border-border"
      >
        <Table>
          <TableHeader data-testid="common-table-header">
            <TableRow className="hover:tw-bg-transparent">
              {tableHeaderData.map(renderHeaderCell)}
            </TableRow>
          </TableHeader>
          <TableBody data-testid="common-table-body">
            {isLoading ? renderLoadingRows() : renderDataRows()}
          </TableBody>
          <TableFooter>
            <TableRow className="hover:tw-bg-transparent">
              <TableCell colSpan={visibleCols.length + 3} className="tw-bg-background tw-p-5">
                {tableFooterContent}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

export default RecipeVariationTable;
